import { Box, Button, Typography, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import TryAgainMessageBlock from '../../common/TryAgainMessageBlock'
import DataLoadingSpinner from '../../common/DataLoadingSpinner'
import noAssignmentImg from '../../../assets/noassignment.svg'
import StudyMaterialViewer from './StudyMaterialViewer'
import { formatDate } from '../../../utils/utilities'
import CustomTable from '../../common/custom-table/CustomTable'
import api from '../../../service/api'
import { useUserStore } from '../../../store/user.store'

const columns = [
    { id: 2, field: "study_material_name", name: "Study Material", width: "120px", padding: "10px 20px" },
    { id: 3, cellStyles: { textTransform: "capitalize" }, renderCell: row => row.study_material_created_by?.toLowerCase(), linkGetter: row => `/${window.location.pathname?.includes( 'admin' ) ? "admin" : "audit"}/faculty/${row.empcode}?legacy=${row.legacy_system_material}`, field: "faculty_name", name: 'Faculty name', width: "150px", align: "left", padding: "10px 20px" },
    { id: 6, cellStyles: { textTransform: "capitalize" }, renderCell: row => `${row?.institute} - ${row?.department_name?.toLowerCase()}`, field: "department", name: 'Department', padding: "10px 20px" },
    { id: 4, renderCell: row => row?.subject_name || "Not available", field: "subject", name: "Subject", padding: "10px 20px" },
    { id: 5, renderCell: row => formatDate( row.study_material_published_date ), field: "created_at", align: "center", name: "Created on", width: "150px", padding: "10px 20px" },
    { id: 7, field: "attachments_count", align: "center", name: "Attachments", padding: "10px 20px" },
]


const AuditStudyMaterialsList = () => {


    const [searchParams, setSearchParams] = useSearchParams( { searchText: "", month: "", selectedDepartment: "all", selectedInstitute: "all", view: "tabular", sortByName: "1", sortBySubmissionsCount: "1", sortByAssignmentCount: "1" } )
    const selectedInstitute = searchParams.get( 'selectedInstitute' ) || "all"

    const [error, setError] = useState( null )
    const [loading, setLoading] = useState( true )
    const [studyMaterials, setStudyMaterials] = useState( [] )
    const [selectedStudyMaterial, setSelectedStudyMaterial] = useState( null )

    const { layout } = useTheme()

    const navigate = useNavigate()

    const UserStore = useUserStore()

    const removeAppliedFilters = () => {
        setSearchParams( prev => {
            prev.set( 'searchText', '' )
            prev.set( 'selectedInstitute', "all" )
            prev.set( 'selectedDepartment', "all" )
            return prev
        } )
    }

    const onAfterDeleteMaterial = ( deleted ) => {
        const newStudyMaterielList = [...studyMaterials]
        setStudyMaterials( newStudyMaterielList.filter( s => s.study_material_id === deleted.study_material_id ) )
    }

    const handleDialogClose = () => {
        setSelectedStudyMaterial( null )
    }


    const getData = useCallback( async () => {
        try {
            setLoading( true )
            const { data: { data } } = await api.audit.getStudyMaterialsForAuditing( { institute: selectedInstitute !== "all" ? selectedInstitute : "" } )
            setStudyMaterials( data )
            setError( null )
        } catch ( err ) {
            setError( { code: err?.response?.status, message: err?.message?.data?.message || "Something went wrong while fetching studyMaterials." } )
        } finally {
            setLoading( false )
        }
    }, [selectedInstitute] )

    useEffect( () => {
        getData()
    }, [getData] )

    return (
        <Box overflow="auto" gap={layout.cardsGap} minHeight="450px" flexGrow={1} display="flex" flexDirection="column">

            {selectedStudyMaterial && <StudyMaterialViewer onAfterDeleteMaterial={onAfterDeleteMaterial} studyMaterial={selectedStudyMaterial} handleClose={handleDialogClose} openState={Boolean( selectedStudyMaterial )} />}

            {/* Heading */}
            <Box display="flex" gap="20px" alignItems="flex-end" justifyContent="space-between" >
                <Typography variant="h1" fontWeight="600" component="h2" fontSize="14px">Study materials</Typography>
                <Button size="small" onClick={() => navigate( `/${["ADMIN", "SUBADMIN"].includes( UserStore.getUser.user_role?.toUpperCase() ) ? "admin" : "audit"}/study-materials` )} variant="contained" color='secondary' disableElevation sx={{ height: "33.5px", textTransform: "capitalize", }} >View Stats</Button>
            </Box >

            {/* Data */}
            {!loading && studyMaterials.length > 0 && <CustomTable dataIdKey="study_material_id" globalSearch="Search by study material, faculty, ID or subject" onRowClick={setSelectedStudyMaterial} containerStyles={{ maxHeight: "450px" }} data={studyMaterials} columns={columns} pageSize={50} />}

            {/* No Data */}
            {!loading && studyMaterials.length === 0 && <Box flexDirection="column" padding="20px" display="flex" alignItems="center" justifyContent="center" gap="20px">
                <Box component="img" src={noAssignmentImg} height="120px" alt="No assignmets to show" />
                <Typography textAlign="center" variant='subtitle2'>No study materials were found for applied filters!</Typography>
                {studyMaterials.length > 0 && <Button onClick={removeAppliedFilters} sx={{ textTransform: "capitalize" }} color="secondary" variant="contained" disableElevation>Remove filters</Button>}
            </Box>}

            {/* Retry */}
            {error && <TryAgainMessageBlock getData={getData} err={error.message} code={error.code} loading={loading} />}

            {/* Loading */}
            {loading && !error && <DataLoadingSpinner padding="0" waitingMessage="Loading study materials please wait...." />}
        </Box>
    )
}

export default AuditStudyMaterialsList
